import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard, ShieldCheck, Eye, RefreshCw, Siren, FolderArchive,
  CalendarClock, Info, Sparkles, ChevronRight,
} from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList,
} from 'recharts'
import { assessmentsApi } from '@/lib/api'
import { useEntitlements } from '@/hooks/use-entitlements'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Callout } from '@/components/ui/callout'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { RegistryStato } from '@/types'

// Status colors from the .italia verdict tokens; VIETATO uses a step lighter
// than rosso-dark so the mark stays inside the chart lightness band
// (validated with the palette checker; identity is carried by axis labels
// and per-bar counts, never by color alone).
const VERDICT_COLORS: Record<string, string> = {
  VERDE: '#007A52',
  GIALLO: '#CC7A00',
  ROSSO: '#CC334D',
  VIETATO: '#992640',
}
const STATO_DOT: Record<RegistryStato, string> = {
  AUTORIZZATO: 'bg-verde',
  CON_LIMITAZIONI: 'bg-giallo-dark',
  SOSPESO: 'bg-giallo',
  DA_RIVALUTARE: 'bg-mim-orange',
  VALIDATO_DPO: 'bg-mim-blue',
  ANALIZZATO: 'bg-muted-foreground/50',
  NON_AUTORIZZATO: 'bg-rosso',
  REVOCATO: 'bg-rosso-dark',
}
const STATO_ORDER: RegistryStato[] = [
  'AUTORIZZATO', 'CON_LIMITAZIONI', 'SOSPESO', 'DA_RIVALUTARE',
  'VALIDATO_DPO', 'ANALIZZATO', 'NON_AUTORIZZATO', 'REVOCATO',
]

function StatTile({
  title, value, icon: Icon, color, onClick,
}: { title: string; value: number; icon: React.ElementType; color: string; onClick?: () => void }) {
  return (
    <Card
      className={cn(
        'border-l-4 transition-all duration-200',
        color.replace('bg-', 'border-l-'),
        onClick && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md'
      )}
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-3 pt-5 pb-5">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center shadow-md shadow-black/10 ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold tabular-nums leading-none">{value}</p>
          <p className="text-xs text-muted-foreground mt-1">{title}</p>
        </div>
      </CardContent>
    </Card>
  )
}

export default function Governance() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'it' ? 'it-IT' : 'en-GB'
  const navigate = useNavigate()
  const { data: ent, isLoading: entLoading } = useEntitlements()
  const isPaid = !!ent && ent.plan !== 'FREE'

  const { data, isLoading, isError } = useQuery({
    queryKey: ['governance'],
    queryFn: () => assessmentsApi.governance(),
    enabled: isPaid,
    retry: false,
    // Backlog/coverage stats are compliance-critical and nothing invalidates
    // this cache when a DPO/Dirigente decision happens elsewhere in the app
    // (unlike the Registro's own actions): every visit must reread it rather
    // than trust the app-wide 2-minute cache (main.tsx).
    staleTime: 0,
  })

  const verdictData = data
    ? (['VERDE', 'GIALLO', 'ROSSO', 'VIETATO'] as const)
        .map((v) => ({
          name: t(`dashboard.charts.${v.toLowerCase()}` as any),
          key: v,
          count: data.verdetti[v] ?? 0,
        }))
    : []

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '0.5rem',
    fontSize: '12px',
    color: 'hsl(var(--popover-foreground))',
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="it-page-title">
        <p className="text-[11px] font-bold uppercase tracking-wider text-mim-blue dark:text-primary mb-1">
          {t('registro.eyebrow')}
        </p>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <LayoutDashboard className="w-6 h-6 text-mim-blue dark:text-primary" />
          {t('governance.title')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {data ? `${data.school_name} — ` : ''}
          {t('governance.subtitle')}
        </p>
      </div>

      {entLoading || (isPaid && isLoading) ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
          <Skeleton className="h-72 rounded-xl" />
        </div>
      ) : !isPaid ? (
        <Callout variant="note" title={t('governance.freeTitle')} icon={<Sparkles className="w-4 h-4" />}>
          {t('governance.freeBody')}
        </Callout>
      ) : isError || !data ? (
        <Callout variant="note" title={t('governance.noSchoolTitle')} icon={<Info className="w-4 h-4" />}>
          {t('governance.noSchoolBody')}
        </Callout>
      ) : (
        <>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatTile title={t('governance.tiles.censiti')} value={data.strumenti_totali}
                      icon={ShieldCheck} color="bg-mim-blue" onClick={() => navigate('/registro')} />
            <StatTile title={t('governance.tiles.daValidare')} value={data.da_validare_dpo}
                      icon={Eye} color="bg-mim-azure" onClick={() => navigate('/review-queue')} />
            <StatTile title={t('governance.tiles.daAutorizzare')} value={data.da_autorizzare_ds}
                      icon={Eye} color="bg-giallo-dark" onClick={() => navigate('/review-queue')} />
            <StatTile title={t('governance.tiles.daRivalutare')} value={data.da_rivalutare}
                      icon={RefreshCw} color="bg-mim-orange" onClick={() => navigate('/registro')} />
            <StatTile title={t('governance.tiles.incidenti')} value={data.incidenti_aperti}
                      icon={Siren} color="bg-rosso" onClick={() => navigate('/registro')} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Verdict distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('governance.verdictTitle')}</CardTitle>
                <CardDescription className="text-xs">
                  {t('governance.verdictDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.strumenti_totali === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-10">
                    {t('governance.verdictEmpty')}
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={verdictData} margin={{ top: 18, right: 8, left: -22, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                             axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                             axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                               formatter={(value: number) => [value, t('governance.tooltipTools')]} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={56} animationDuration={700}>
                        <LabelList dataKey="count" position="top"
                                   style={{ fontSize: 12, fill: 'hsl(var(--foreground))' }} />
                        {verdictData.map((d) => (
                          <Cell key={d.key} fill={VERDICT_COLORS[d.key]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Register coverage + states */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('governance.coverageTitle')}</CardTitle>
                <CardDescription className="text-xs">
                  {t('governance.coverageDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-end gap-3">
                  <p className="text-4xl font-bold tabular-nums leading-none">
                    {data.copertura_percento}%
                  </p>
                  <p className="text-xs text-muted-foreground pb-0.5">
                    {t('governance.coverageOf', { decided: data.copertura_decisi, total: data.strumenti_totali })}
                  </p>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-mim-blue transition-all"
                    style={{ width: `${data.copertura_percento}%` }}
                  />
                </div>
                <div className="space-y-1.5 pt-1">
                  {STATO_ORDER.filter((s) => (data.registro[s] ?? 0) > 0).map((s) => (
                    <div key={s} className="flex items-center gap-2 text-sm">
                      <span className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', STATO_DOT[s])} />
                      <span className="flex-1">{t(`registro.stato.${s}` as any)}</span>
                      <span className="tabular-nums font-medium">{data.registro[s]}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-1">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  {t('governance.coverageNote')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Re-evaluation deadlines */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-mim-blue dark:text-primary" />
                {t('governance.deadlinesTitle')}
              </CardTitle>
              <CardDescription className="text-xs">
                {t('governance.deadlinesDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.scadenze.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {t('governance.deadlinesEmpty')}
                </p>
              ) : (
                <div className="divide-y divide-border/60">
                  {data.scadenze.map((s) => (
                    <button
                      key={s.assessment_id}
                      onClick={() => navigate(`/assessment/${s.assessment_id}`)}
                      className="flex items-center gap-3 w-full py-2.5 text-left text-sm hover:bg-muted/40 rounded-md px-2 transition-colors"
                    >
                      <span className="flex-1 font-medium truncate">{s.strumento}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(s.valid_until).toLocaleDateString(locale)}
                      </span>
                      <Badge className={cn(
                        'text-xs',
                        s.scaduta ? 'bg-rosso text-white' : 'bg-giallo text-white'
                      )}>
                        {s.scaduta ? t('governance.expired') : t('governance.daysLeft', { count: s.days_left })}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <FolderArchive className="w-3.5 h-3.5" />
            {t('governance.docsFooter', { count: data.documenti_totali })}
          </p>
        </>
      )}
    </div>
  )
}
